<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { ethers } from "ethers";
import { ChevronDown, Wallet, ArrowLeft, ArrowRight, CircleCheck } from "lucide-vue-next";
import { Currency } from "fungi-types";
import usePOLPrice from "@/stores/POLprice";

const { showOverlay: showRampOverlay } = useRamp();
const { showOverlay: showTransakOverlay } = useTransak();
import { useRamp } from "@/composables/useRamp";
import { useMediaQuery } from "~/composables/useMediaQuery";
import { useTransak } from "@/composables/useTransak";
import { formatNumberLocal } from "~/utils/math";

import Modal from "@/components/modals/ModalContainer.vue";
import { useStoreUserV2 } from "~/stores/storeUserV2";
import { polygonscanUrl } from "@/application/config";
import { showNotification, ToastType } from "~/shadcn/components/ui/sonner/custom-toast";

const { t } = useI18n();
const storeUser = useStoreUserV2();
const walletAddress = ref("");
import { useWeb3AuthStore } from "~/stores/storeWeb3auth";
const web3AuthStore = useWeb3AuthStore();

import LogoTransak from "/logo-transak.svg?url";
import LogoRamp from "/logo-ramp.svg?url";
import { parseWalletAddress } from "~/utils/wallets";

const gasFee = ref(0);
const amount = ref(0);

const targetAmount = computed((): number => {
  return amount.value - gasFee.value;
});

const txUrl = computed((): string => {
  return `${polygonscanUrl}tx/${txHash.value}`;
});

onMounted(() => {
  gasEstimation();
  storeUser.refreshBalance();
  if (props.smartWalletMigration && web3AuthStore.smartWalletAddress) {
    sendToExternalWalletMode.value = true;
    walletAddress.value = web3AuthStore.smartWalletAddress;
    amount.value = Number(userBalanceInSelectedCurrency.value);
    goNext();
  }
});

const isValidWalletAddress = computed((): boolean => {
  return ethers.utils.isAddress(walletAddress.value);
});

const insufficientFunds = computed((): boolean => {
  return amount.value > Number(userBalanceInSelectedCurrency.value);
});

const resetValues = () => {
  walletAddress.value = "";
  amount.value = 0;
};

const loading = ref(false);
const txHash = ref("");

async function transferCryptoToWallet() {
  try {
    loading.value = true;
    if (!isValidWalletAddress.value) {
      showNotification({ type: ToastType.ERROR, message: t("messages.invalid-wallet-address") });
      return;
    }

    if (targetAmount.value <= 0) {
      showNotification({ type: ToastType.ERROR, message: t("messages.invalid-amount") });
      return;
    }

    console.log("Transferring", targetAmount.value, "to", walletAddress.value);

    const txHashResp = await web3AuthStore.transferCrypto({
      toAddress: walletAddress.value,
      amount: targetAmount.value,
      tokenSymbol: selectedCurrency.value.id,
    });

    txHash.value = txHashResp;

    console.log("txHash", txHash.value);

    showNotification({
      type: ToastType.SUCCESS,
      title: t("messages.funds-transfer-success"),
      message: t("messages.funds-transfer-success-details", {
        amount: targetAmount.value.toFixed(8),
        walletAddress: walletAddress.value,
      }),
      duration: 5_000,
    });

    await storeUser.refreshBalance();
    resetValues();
  } catch (e) {
    console.error("Error transferring funds to wallet:", e);
    showNotification({
      type: ToastType.ERROR,
      title: t("messages.funds-transfer-failed"),
      message: t("messages.funds-transfer-failed-details"),
      duration: 5_000,
    });
  } finally {
    loading.value = false;
  }
}

async function gasEstimation() {
  const gasEst = await web3AuthStore.estimatePolTransferGas();
  gasFee.value = gasEst.gasFeeNumber;
}

const emit = defineEmits(["onClose"]);
const handleOnClose = () => {
  if (loading.value) return;
  emit("onClose");
};

const props = withDefaults(
  defineProps<{
    show: boolean;
    smartWalletMigration?: boolean;
  }>(),
  {
    show: false,
  }
);

const isMobile = useMediaQuery("(max-width: 800px)");
const device = computed(() => (isMobile.value ? "mobile" : "desktop"));

const selectRamp = () => {
  showRampOverlay({
    device: device.value,
    mode: "sell",
  });
  handleOnClose();
};

const selectTransak = () => {
  showTransakOverlay({
    device: device.value,
    mode: "sell",
  });
  handleOnClose();
};

const sendToExternalWalletMode = ref(false);
const previewTransfer = ref(false);

const toggleSendToExternalWalletMode = () => {
  sendToExternalWalletMode.value = !sendToExternalWalletMode.value;
};

const goBack = () => {
  if (sendToExternalWalletMode.value) {
    if (props.smartWalletMigration) {
      handleOnClose();
      return;
    }
    sendToExternalWalletMode.value = false;
    previewTransfer.value = false;
  } else if (previewTransfer.value) {
    previewTransfer.value = false;
    sendToExternalWalletMode.value = true;
  }
};

const goNext = () => {
  if (sendToExternalWalletMode.value) {
    gasEstimation();
    previewTransfer.value = true;
    sendToExternalWalletMode.value = false;
  } else if (previewTransfer.value) {
    transferCryptoToWallet();
  }
};

const currenciesOptions = [
  {
    id: Currency.POL,
    label: "POL",
    value: Currency.POL,
  },
  {
    id: Currency.WPOL,
    label: "WPOL",
    value: Currency.WPOL,
  },
  {
    id: Currency.USDC,
    label: "USDC",
    value: Currency.USDC,
  },
];

const selectedCurrency = ref(currenciesOptions[2]);
const handleOnchange = (selection: { id: Currency.POL; label: string; value: Currency.POL }) => {
  selectedCurrency.value = selection;
};

const logoUrl = computed(() => {
  switch (selectedCurrency.value.value) {
    case Currency.POL:
      return "/images/pol-token.svg";
    case Currency.WPOL:
      return "/images/wpol.svg";
    case Currency.USDC:
      return "/images/usdc.svg";
    default:
      return "/images/usdc.svg";
  }
});

const userBalanceInSelectedCurrency = computed((): number | string => {
  switch (selectedCurrency.value.value) {
    case Currency.POL:
      return parseFloat(web3AuthStore.polRawBalance) / 1_000_000;
    case Currency.WPOL:
      return parseFloat(web3AuthStore.wpolRawBalance) / 1_000_000;
    case Currency.USDC:
      return parseFloat(web3AuthStore.usdcRawBalance) / 1_000_000;
    default:
      return 0;
  }
});

const setMaxAmount = () => {
  amount.value = Number(userBalanceInSelectedCurrency.value);
};

const amountInUSD = computed((): number => {
  const polPriceStore = usePOLPrice();
  const rate = polPriceStore.rates.usd;
  let value = 0;
  let amountVal = amount.value || 0;

  switch (selectedCurrency.value.value) {
    case Currency.POL:
      value = amountVal * rate;
      break;
    case Currency.WPOL:
      value = amountVal * rate;
      break;
    case Currency.USDC:
      value = amountVal;
      break;
    default:
      value = amountVal;
      break;
  }
  console.log("amountInUSD", value);
  return parseFloat(value?.toFixed(2) || "0");
});

const recipientPretty = computed(() => {
  return parseWalletAddress(walletAddress.value.toLowerCase());
});

const title = computed(() => {
  if (props.smartWalletMigration) {
    return "Transfer USDC to new Account";
  } else {
    return t("_token-transfer.withdraw.title");
  }
});
</script>

<template>
  <Modal :show="show" @onClose="handleOnClose" :title="title">
    <!-- :title="t('modals.transfer.mtc-from-wallet-to-another')" -->
    <div class="flex flex-col gap-2">
      <template v-if="previewTransfer">
        <div class="flex flex-col w-full justify-center items-center gap-2 mb-3">
          <img
            :src="logoUrl"
            :alt="selectedCurrency.label"
            class="w-10 h-10 inline-block rounded-full"
          />
          <div class="items-center justify-center flex-col text-center">
            <div class="text-3xl font-bold">{{ amount }} {{ selectedCurrency.label }}</div>
            <div class="text-sm text-zinc-400">~ ${{ amountInUSD }}</div>
          </div>
        </div>
        <div class="bg-zinc-800 rounded-md w-full flex justify-between p-4 py-3 gap-1 text-sm">
          <div>
            <span class="font-medium text-sm text-zinc-400">{{
              t("_token-transfer.withdraw.recipient")
            }}</span>
          </div>
          <div>
            <span>{{ recipientPretty.start }}</span
            ><span class="text-zinc-500"> ... </span><span>{{ recipientPretty.end }}</span>
          </div>
        </div>
        <div class="bg-zinc-800 rounded-md w-full flex justify-between p-4 py-3 gap-1 text-sm">
          <div>
            <span class="font-medium text-sm text-zinc-400">Network</span>
          </div>
          <div>
            <span>Polygon</span>
            <img
              src="/images/pol-token.svg"
              alt="Polygon"
              class="w-5 h-5 inline-block ml-1 rounded-full"
            />
          </div>
        </div>
        <div class="bg-zinc-800 rounded-md w-full flex justify-between p-4 py-3 gap-1 text-sm">
          <div>
            <span class="font-medium text-zinc-400">
              {{ t("_token-transfer.estimated-gas-fee") }}
            </span>
          </div>
          <div class="flex gap-1 items-center">
            <span class="text-zinc-400">
              {{ gasFee < 0.01 ? `< ${formatNumberLocal(0.01)}` : `~${formatNumberLocal(gasFee)}` }}
              $US</span
            >
            <img src="/images/pol-token.svg" alt="Polygon" class="w-4 h-4 rounded-full" />
            POL
          </div>
        </div>
      </template>
      <template v-else-if="sendToExternalWalletMode">
        <div class="grid gap-4">
          <div v-if="!props.smartWalletMigration">
            <div class="mb-1">{{ t("_token-transfer.recipient") }}</div>
            <input
              class="w-full p-0 !bg-transparent focus:!outline-none border rounded px-4 text-sm py-3"
              :class="{
                'border-red-500': walletAddress && !isValidWalletAddress,
                'border-gray-600': !walletAddress || isValidWalletAddress,
              }"
              :placeholder="t('_token-transfer.enter-or-paste-wallet-address')"
              name="walletAddress"
              type="text"
              v-model="walletAddress"
            />
            <div v-if="walletAddress && !isValidWalletAddress" class="text-error text-xs mt-1">
              {{ t("_token-transfer.invalid-wallet-address") }}
            </div>
            <div v-else class="text-warning text-xs mt-1">
              {{ t("_token-transfer.withdraw.warning") }}
            </div>
          </div>
          <div>
            <div class="mb-1">{{ t("_payment.amount") }}</div>
            <div class="border-gray-600 rounded pl-2 w-full border flex items-center py-1">
              <input
                class="w-full p-0 !bg-transparent focus:!outline-none px-2 text-sm"
                placeholder="0"
                name="walletAddress"
                type="number"
                :valid="isValidWalletAddress"
                :min="0"
                :max="userBalanceInSelectedCurrency"
                v-model="amount"
              />

              <div class="flex items-center h-10 py-1 px-1">
                <InputSelect
                  :selectedId="selectedCurrency.id"
                  @onChange="handleOnchange"
                  :options="currenciesOptions"
                  :rounded="false"
                  position="top"
                  class="position-relative z-50 group"
                >
                  <div class="flex items-center cursor-pointer hover:bg-zinc-900 h-10 px-2 rounded">
                    <img :src="logoUrl" alt="POL" class="w-5 h-5 inline-block rounded-full" />
                    <ChevronDown class="h-4 ml-1 text-white" />
                  </div>
                </InputSelect>
              </div>
            </div>
            <div class="w-full justify-between flex text-xs mt-1 text-zinc-400 px-1">
              <div v-if="insufficientFunds" class="text-red-500">
                {{ t("_payment.insufficient-funds") }}
              </div>
              <div v-else>${{ amountInUSD }}</div>
              <div class="flex gap-1">
                <div>{{ t("_token-transfer.available") }}: {{ userBalanceInSelectedCurrency }}</div>
                <div @click="setMaxAmount" class="cursor-pointer hover:underline text-blue-500">
                  Max.
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <p class="text-xs text-justify text-neutral-500 mb-2">
          {{ $t("modals.buyMatic.description") }}
        </p>
        <div class="flex flex-col gap-4 mb-4">
          <ButtonPrimary @click="toggleSendToExternalWalletMode" class="text-left px-3">
            <div class="flex items-center gap-2">
              <div class="p-1 bg-zinc-800 rounded-full w-7 h-7 flex items-center justify-center">
                <Wallet class="h-4 text-white" />
              </div>
              {{ t("_token-transfer.withdraw.send-to-external-wallet") }}
            </div>
          </ButtonPrimary>
          <ButtonPrimary @click="selectRamp" class="text-left px-3" disabled>
            <img :src="LogoRamp" alt="Ramp" class="w-6 h-6 mr-2 inline-block" />
            {{ $t("modals.buyMatic.useRamp") }}
            <span class="text-yellow-600 font-normal text-sm"> - Available soon</span>
          </ButtonPrimary>
          <ButtonPrimary @click="selectTransak" class="text-left px-3" disabled>
            <img :src="LogoTransak" alt="Ramp" class="w-6 h-6 mr-2 inline-block" />
            {{ $t("modals.buyMatic.useTransak") }}
            <span class="text-yellow-600 font-normal text-sm"> - Available soon</span>
          </ButtonPrimary>
        </div>
      </template>

      <!-- Transactin SuccessFull -->
      <div v-if="txHash">
        <div class="text-center">
          <div class="h-[1px] w-full bg-zinc-800 my-2"></div>
          <div class="text-lg font-semibold flex items-center justify-center gap-2 rounded py-2">
            <CircleCheck class="h-6 w-6 text-green-500 rounded-full" />
            {{ t("_token-transfer.transfer-Successful") }}!
          </div>
          <div class="text-sm text-zinc-400 items-center justify-center flex-col">
            <div class="flex gap-1 w-full justify-center items-center">
              {{ t("_token-transfer.view-details-on") }}
              <a
                :href="txUrl"
                target="_blank"
                class="text-blue-500 hover:underline flex items-center"
              >
                PolygonScan
                <icon-mi:external-link class="inline h-4 w-4 ml-1" />
              </a>
            </div>
          </div>
        </div>
        <Button type="secondary" class="w-full mt-4" size="sm" @click="handleOnClose">
          <div class="w-full text-center">{{ t("_shared.close") }}</div>
        </Button>
      </div>
      <div
        v-else-if="sendToExternalWalletMode || previewTransfer"
        class="grid grid-cols-2 gap-4 mt-6"
      >
        <Button type="secondary" class="w-full" @click="goBack" :disabled="loading">
          <div class="flex gap-0 w-full justify-center items-center">
            <ArrowLeft class="h-4" />
            <div class="w-full text-center">
              <template v-if="previewTransfer">
                {{ t("_shared.update") }}
              </template>
              <template v-else>
                {{ t("_shared.back") }}
              </template>
            </div>
          </div>
        </Button>
        <Button
          type="primary-blue"
          class="w-full"
          @click="goNext"
          :disabled="
            amount <= 0 ||
            loading ||
            insufficientFunds ||
            !isValidWalletAddress ||
            walletAddress.length === 0
          "
          :loading="loading"
        >
          <div class="flex gap-0 w-full justify-center items-center">
            <div class="w-full text-center">
              {{ t("_shared.confirm") }}
            </div>
            <ArrowRight class="h-4" />
          </div>
        </Button>
      </div>
    </div>
  </Modal>
</template>
